// A fronteira do CLIPE de fundo da boas-vindas (spec-009 [US-2], T023) — o contrato que o
// player consome, a normalização do que o provedor devolve e a degradação.
//
// ★★ POR QUE NÃO HÁ CHAMADA AO VIGGLE AQUI, E NÃO PODE HAVER. A autenticação do vendor é
// `Authorization: Bearer <project API key>`, e o próprio contrato dele manda, em
// `securitySchemes`: **"Never expose a project API key in browser code."** Não há token
// efêmero nem URL assinada para CRIAR geração — as short-lived URLs do contrato são só
// para BAIXAR o resultado pronto. Então a geração é do backend, sem alternativa, e o que
// mora no front é esta fronteira: pedir o clipe JÁ GERADO e saber o que fazer com cada
// resposta. O precedente do repo é o mesmo desenho: em `fee-fixo/.../conversa-fluida.js`
// o browser fala com o ElevenLabs, mas a credencial vem assinada do backend — a chave da
// conta nunca sai do servidor.
//
// ★ QUEM BUSCA É INJETADO (`criarProvedorDeClipe({ buscar })`). É isto que faz a troca de
// provedor não tocar o fluxo do chat: HeyGen, D-ID ou Hedra entram como outro `buscar`,
// e tanto o player quanto o host continuam iguais. Um segundo provedor precisa de UMA
// coisa: uma função que devolva `{ status, url, expira_em }` — nada mais.
//
// ★ NINGUÉM ESPERA PELO CLIPE. A geração é assíncrona (minutos) e o vídeo é feito UMA vez
// por profissional e reaproveitado; a primeira mensagem nunca fica parada por ele. Por
// isso `obter()` NUNCA rejeita: provedor fora do ar, chave ausente no servidor, geração em
// curso e clipe inexistente devolvem, todos, um resultado que o player sabe ignorar.

// Os estados que o front conhece. Os três últimos têm o MESMO efeito na tela (segue sem
// clipe) e são separados porque a causa é diferente — e um dia alguém vai querer saber.
export const CLIPE_PRONTO = 'pronto'
export const CLIPE_GERANDO = 'gerando'
export const CLIPE_AUSENTE = 'ausente'
export const CLIPE_INDISPONIVEL = 'indisponivel'

// Mapa dos estados do VENDOR (`RenderStatus` da OpenAPI do Viggle:
// queued · processing · ready · failed · cancelled) para os nossos. O contrato dele
// instrui literalmente: "Treat an unrecognized value as 'in progress' rather than
// failing" — por isso o default é `gerando`, e não `indisponivel`.
const DO_VENDOR = {
  ready: CLIPE_PRONTO,
  queued: CLIPE_GERANDO,
  processing: CLIPE_GERANDO,
  failed: CLIPE_INDISPONIVEL,
  cancelled: CLIPE_INDISPONIVEL,
}

const VAZIO = { status: CLIPE_AUSENTE, url: '', expiraEm: null }

function semClipe(status) {
  return { status, url: '', expiraEm: null }
}

// A URL do clipe é SHORT-LIVED por contrato do vendor ("Short-lived URL of the completed
// video; null until ready"). Uma URL vencida não é um clipe: é um 403 esperando para
// acontecer no meio da boas-vindas.
function vencida(expiraEm, agora) {
  if (!expiraEm) return false
  const prazo = Date.parse(expiraEm)
  if (isNaN(prazo)) return false
  return prazo <= agora
}

// Tira o clipe de dentro do envelope AAWZ (`{ status, value }`) ou aceita o objeto do
// clipe direto — o backend envelopa, um provedor futuro pode não envelopar. Devolve
// `null` quando não há clipe e `false` quando o ENVELOPE falhou (que é outra história:
// indisponibilidade do provedor, não ausência de clipe).
function clipeDaResposta(resposta) {
  if (!resposta || typeof resposta !== 'object') return null
  const temEnvelope = typeof resposta.status === 'number'
  if (temEnvelope && resposta.status !== 200) return false
  const clipe = temEnvelope ? resposta.value : resposta
  if (!clipe || typeof clipe !== 'object') return null
  return clipe
}

// O estado de um clipe SEM URL utilizável. `status` que o provedor não declarou é
// ausência; declarado mas desconhecido é "em curso" (instrução do contrato do vendor).
function estadoSemUrl(clipe, estadoDoVendor) {
  if (estadoDoVendor) return estadoDoVendor
  return clipe.status ? CLIPE_GERANDO : CLIPE_AUSENTE
}

/**
 * Normaliza o que o provedor devolveu para o contrato que o player consome.
 */
export function normalizarClipe(resposta, { agora = Date.now() } = {}) {
  const clipe = clipeDaResposta(resposta)
  if (clipe === false) return semClipe(CLIPE_INDISPONIVEL)
  if (!clipe) return VAZIO

  const estado = DO_VENDOR[String(clipe.status || '').toLowerCase()]
  if (estado === CLIPE_INDISPONIVEL) return semClipe(CLIPE_INDISPONIVEL)

  const url = String(clipe.url || '').trim()
  // ★ O STATUS DECLARADO VENCE A URL. Um `queued`/`processing` que venha acompanhado de
  // url não é um clipe tocável: o vendor está dizendo, com todas as letras, que não
  // terminou. Hoje o backend não produz esse par (`_com_url` hardcoda `ready` e
  // `_sem_clipe` sempre manda `url: None`), mas "inalcançável porque o produtor atual não
  // emite" é garantia que some quando alguém mexe no produtor — e o defeito que ela evita
  // seria tocar um vídeo pela metade no primeiro contato do consultor com a plataforma.
  // Status NÃO declarado continua deixando a url mandar: aí não há vendor dizendo nada,
  // e é o caso do provedor futuro que só devolve o arquivo.
  const naoTerminou = Boolean(clipe.status) && estado !== CLIPE_PRONTO
  if (!url || naoTerminou) return semClipe(estadoSemUrl(clipe, estado))

  const expiraEm = clipe.expira_em || clipe.expiraEm || null
  if (vencida(expiraEm, agora)) return semClipe(CLIPE_INDISPONIVEL)

  return { status: CLIPE_PRONTO, url, expiraEm }
}

/**
 * A URL que o player recebe — `''` em tudo que não está pronto.
 *
 * O player trata `''` como "não há clipe" e mostra o retrato (ou só a legenda): é o mesmo
 * caminho de quem nunca teve clipe. Nenhum estado intermediário vaza para a tela.
 */
export function urlDoClipe(resultado) {
  if (!resultado || resultado.status !== CLIPE_PRONTO) return ''
  return resultado.url || ''
}

/**
 * O provedor: recebe QUEM busca e devolve `obter()`, que nunca rejeita.
 *
 * @param {Object} opcoes
 * @param {Function=} opcoes.buscar Devolve (ou promete) `{ status, url, expira_em }` — ou
 *   o envelope AAWZ com isso no `value`. Ausente = não há provedor configurado, e isso é
 *   um estado normal, não um erro.
 * @param {Function=} opcoes.aoFalhar Chamado com o erro quando `buscar` lança. Existe para
 *   quem quiser registrar a falha; o fluxo segue igual com ou sem ele.
 */
export function criarProvedorDeClipe({ buscar = null, aoFalhar = null } = {}) {
  return {
    async obter() {
      // Sem provedor configurado não é falha: é a plataforma rodando sem o vendor, que é
      // o estado esperado enquanto a integração não sobe.
      if (typeof buscar !== 'function') return VAZIO
      try {
        return normalizarClipe(await buscar())
      } catch (erro) {
        // ★ A degradação é o contrato: o vendor caindo não pode derrubar a boas-vindas.
        if (typeof aoFalhar === 'function') aoFalhar(erro)
        return semClipe(CLIPE_INDISPONIVEL)
      }
    },
  }
}
